// Frontend wrappers for escalation-policy callables and Firestore reads.

import { httpsCallable } from 'firebase/functions';
import { getDoc } from 'firebase/firestore';
import { functions } from '../firebase';
import { orgEscalationPolicyDoc } from './firestore';
import type { EscalationPolicy, EscalationStep } from './types';

interface CreateRequest {
  orgId: string;
  name: string;
  steps: EscalationStep[];
}

export async function createEscalationPolicy(
  orgId: string,
  input: { name: string; steps: EscalationStep[] },
): Promise<string> {
  const callable = httpsCallable<CreateRequest, { policyId: string }>(
    functions,
    'createEscalationPolicy',
  );
  const result = await callable({ orgId, ...input });
  return result.data.policyId;
}

export async function updateEscalationPolicy(
  orgId: string,
  policyId: string,
  updates: { name?: string; steps?: EscalationStep[] },
): Promise<void> {
  const callable = httpsCallable(functions, 'updateEscalationPolicy');
  await callable({ orgId, policyId, ...updates });
}

export async function deleteEscalationPolicy(orgId: string, policyId: string): Promise<void> {
  const callable = httpsCallable(functions, 'deleteEscalationPolicy');
  await callable({ orgId, policyId });
}

export async function getEscalationPolicy(
  orgId: string,
  policyId: string,
): Promise<EscalationPolicy | null> {
  const snap = await getDoc(orgEscalationPolicyDoc(orgId, policyId));
  if (!snap.exists()) return null;
  return readPolicy(snap.data() as DocShape);
}

type DocShape = Omit<EscalationPolicy, 'createdAt' | 'updatedAt'> & {
  createdAt: number | { toMillis(): number };
  updatedAt: number | { toMillis(): number };
};

export function readPolicy(raw: DocShape): EscalationPolicy {
  return {
    ...raw,
    createdAt:
      typeof raw.createdAt === 'number' ? raw.createdAt : raw.createdAt.toMillis(),
    updatedAt:
      typeof raw.updatedAt === 'number' ? raw.updatedAt : raw.updatedAt.toMillis(),
  };
}
